/**
 * useDraft — hook que orquestra o ciclo de vida de um onboarding draft.
 *
 * Lote 7 (ZTNA Wizard). Encapsula:
 *   - GET /admin/ztna/drafts/active — carrega draft em andamento (1 por user).
 *   - POST /admin/ztna/drafts — cria novo draft (lazy, no momento do save).
 *   - PUT  /admin/ztna/drafts/{id}/step/{n} — autosave debounced.
 *   - DELETE /admin/ztna/drafts/{id} — abandonar wizard.
 *   - POST /admin/ztna/onboard — submit final (Step 4).
 *   - POST /admin/ztna/onboard/preflight — checagem antes do submit.
 *
 * Autosave: 1s debounced via setTimeout. Cada step component chama
 * `saveStepDebounced(n, data)` ao editar. Se o draft ainda nao existe,
 * createDraft eh disparado primeiro e o save se encadeia.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ztnaApi } from '../api'
import type {
  DraftResponse,
  JobPayload,
  JobSubmitResponse,
  PreflightResponse,
} from '../types'
import type { DnsMigrationState } from './wizardState'

const DRAFT_QUERY_KEY = ['ztna', 'draft', 'active'] as const
const AUTOSAVE_DEBOUNCE_MS = 1000

export interface UseDraftReturn {
  draft: DraftResponse | null | undefined
  isLoading: boolean
  isSaving: boolean
  isCreating: boolean
  isSubmitting: boolean
  lastSavedAt: Date | null
  error: Error | null
  /** Salva imediatamente (sem debounce). Cria draft se nao existir. */
  saveStep: (stepN: number, stepData: Record<string, unknown>) => Promise<DraftResponse>
  /** Salva apos 1s de inatividade. Cancelado ao chamar de novo. */
  saveStepDebounced: (stepN: number, stepData: Record<string, unknown>) => void
  /** Apaga draft no servidor + invalida cache. */
  deleteDraft: () => Promise<void>
  /** Submete onboarding (Step 4). Marca draft como submitted. */
  submitOnboard: (
    payload: JobPayload,
    dnsMigration?: DnsMigrationState,
  ) => Promise<JobSubmitResponse>
  /** Roda preflight checks contra payload corrente. */
  runPreflight: (payload: JobPayload) => Promise<PreflightResponse>
}

export function useDraft(): UseDraftReturn {
  const queryClient = useQueryClient()
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingSaveRef = useRef<{ n: number; data: Record<string, unknown> } | null>(null)

  const draftQuery = useQuery({
    queryKey: DRAFT_QUERY_KEY,
    queryFn: () => ztnaApi.getActiveDraft(),
    staleTime: 30_000,
  })

  const createMut = useMutation({
    mutationFn: () => ztnaApi.createDraft(),
    onSuccess: (data) => {
      queryClient.setQueryData(DRAFT_QUERY_KEY, data)
    },
    onError: (err: Error) => setError(err),
  })

  const saveMut = useMutation({
    mutationFn: ({ id, n, data }: { id: number; n: number; data: Record<string, unknown> }) =>
      ztnaApi.saveStep(id, n, data),
    onSuccess: (data) => {
      queryClient.setQueryData(DRAFT_QUERY_KEY, data)
      setLastSavedAt(new Date())
      setError(null)
    },
    onError: (err: Error) => setError(err),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => ztnaApi.deleteDraft(id),
    onSuccess: () => {
      queryClient.setQueryData(DRAFT_QUERY_KEY, null)
      setLastSavedAt(null)
    },
  })

  const submitMut = useMutation({
    mutationFn: ({
      payload,
      draftId,
      dnsMigration,
    }: {
      payload: JobPayload
      draftId: number | null
      dnsMigration?: DnsMigrationState
    }) => ztnaApi.submitOnboard(payload, draftId, dnsMigration),
    onSuccess: () => {
      // Invalida pra que /drafts/active retorne null (draft eh apagado server-side).
      queryClient.invalidateQueries({ queryKey: DRAFT_QUERY_KEY })
    },
  })

  const ensureDraft = useCallback(async (): Promise<DraftResponse> => {
    const current = queryClient.getQueryData<DraftResponse | null>(DRAFT_QUERY_KEY)
    if (current) return current
    const created = await createMut.mutateAsync()
    return created
  }, [createMut, queryClient])

  const saveStep = useCallback(
    async (stepN: number, stepData: Record<string, unknown>): Promise<DraftResponse> => {
      const draft = await ensureDraft()
      const result = await saveMut.mutateAsync({ id: draft.id, n: stepN, data: stepData })
      return result
    },
    [ensureDraft, saveMut],
  )

  const saveStepDebounced = useCallback(
    (stepN: number, stepData: Record<string, unknown>) => {
      pendingSaveRef.current = { n: stepN, data: stepData }
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = setTimeout(() => {
        const pending = pendingSaveRef.current
        if (!pending) return
        pendingSaveRef.current = null
        saveStep(pending.n, pending.data).catch((err) => {
          // Erro ja capturado em onError; aqui silencia rejection.
          // eslint-disable-next-line no-console
          if (import.meta.env.DEV) console.warn('[useDraft] autosave failed', err)
        })
      }, AUTOSAVE_DEBOUNCE_MS)
    },
    [saveStep],
  )

  // Cleanup pra evitar autosave depois de unmount.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    }
  }, [])

  const deleteDraft = useCallback(async () => {
    const current = queryClient.getQueryData<DraftResponse | null>(DRAFT_QUERY_KEY)
    if (!current) return
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current)
    pendingSaveRef.current = null
    await deleteMut.mutateAsync(current.id)
  }, [deleteMut, queryClient])

  const submitOnboard = useCallback(
    async (
      payload: JobPayload,
      dnsMigration?: DnsMigrationState,
    ): Promise<JobSubmitResponse> => {
      const current = queryClient.getQueryData<DraftResponse | null>(DRAFT_QUERY_KEY)
      // Flush qualquer autosave pendente antes de submeter.
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
        debounceTimerRef.current = null
      }
      const result = await submitMut.mutateAsync({
        payload,
        draftId: current?.id ?? null,
        dnsMigration,
      })
      return result
    },
    [queryClient, submitMut],
  )

  const runPreflight = useCallback(
    async (payload: JobPayload): Promise<PreflightResponse> => {
      return ztnaApi.preflight(payload)
    },
    [],
  )

  return useMemo(
    () => ({
      draft: draftQuery.data,
      isLoading: draftQuery.isLoading,
      isSaving: saveMut.isPending,
      isCreating: createMut.isPending,
      isSubmitting: submitMut.isPending,
      lastSavedAt,
      error,
      saveStep,
      saveStepDebounced,
      deleteDraft,
      submitOnboard,
      runPreflight,
    }),
    [
      draftQuery.data,
      draftQuery.isLoading,
      saveMut.isPending,
      createMut.isPending,
      submitMut.isPending,
      lastSavedAt,
      error,
      saveStep,
      saveStepDebounced,
      deleteDraft,
      submitOnboard,
      runPreflight,
    ],
  )
}
